import { LayersIcon, PenLineIcon, type LucideIcon } from "lucide-react";

type LucideEntry = { type: "lucide"; icon: LucideIcon };
type ImageEntry = { type: "image"; src: string };
type IconEntry = LucideEntry | ImageEntry;

const ASPI_AVATAR: ImageEntry = { type: "image", src: "/aspi-avatar.png" };

const SERVER_ICONS: Record<string, IconEntry> = {
  praetorCases: { type: "lucide", icon: LayersIcon },
  praetorScribe: { type: "lucide", icon: PenLineIcon },
  aspiCz: ASPI_AVATAR,
  aspiSk: ASPI_AVATAR,
  jogtar: ASPI_AVATAR,
};

/**
 * True iff `name` has a registered icon entry. Lets callers (e.g. the process-window
 * group header) gate their fallback chain before rendering: registered MCP server →
 * branded icon; unknown name → native-tool icon resolver. Without this gate, an
 * unknown server name would render the first-letter placeholder instead of, say,
 * the native ICON_DOT used for `__other__` groups.
 */
export function hasMcpServerIcon(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(SERVER_ICONS, name);
}

export function McpServerIcon({ name, className }: { name: string; className?: string }) {
  const entry = SERVER_ICONS[name];
  if (entry?.type === "lucide") {
    const Icon = entry.icon;
    return <Icon className={className ?? "size-3.5 shrink-0"} aria-label={name} />;
  }
  if (entry?.type === "image") {
    return (
      <img
        src={entry.src}
        alt={name}
        className={className ?? "size-3.5 rounded-sm object-contain shrink-0"}
      />
    );
  }
  return (
    <span className="flex items-center justify-center size-3 rounded-sm bg-foreground/20 text-foreground/60 text-[7px] font-bold shrink-0">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}
